import type { NodeCategory, NodeType } from "@velizhanin-dev/bot-scenario"
import {
	Bell,
	Clock,
	CornerDownRight,
	CreditCard,
	GitBranch,
	Globe,
	LayoutGrid,
	type LucideIcon,
	MessageCircleQuestion,
	MessageSquare,
	Play,
	Shuffle,
	Tag,
	UserCheck,
	Variable,
} from "lucide-react"

/**
 * Цвет категории: плашка иконки на блоке, в меню и в панели блока, точка на миникарте.
 * Цвета приглушённые: на тёмном холсте яркие шапки спорили с текстом.
 */
export const CATEGORY_APPEARANCE: Record<
	NodeCategory,
	{ label: string; tile: string; accent: string; minimap: string }
> = {
	system: {
		label: "Система",
		tile: "bg-slate-400/15 text-slate-300",
		accent: "#94a3b8",
		minimap: "#64748b",
	},
	response: {
		label: "Сообщения",
		tile: "bg-sky-400/15 text-sky-300",
		accent: "#38bdf8",
		minimap: "#0ea5e9",
	},
	input: {
		label: "Ответ пользователя",
		tile: "bg-emerald-400/15 text-emerald-300",
		accent: "#34d399",
		minimap: "#10b981",
	},
	condition: {
		label: "Ветвления",
		tile: "bg-amber-400/15 text-amber-300",
		accent: "#fbbf24",
		minimap: "#f59e0b",
	},
	action: {
		label: "Действия",
		tile: "bg-violet-400/15 text-violet-300",
		accent: "#a78bfa",
		minimap: "#8b5cf6",
	},
}

/** Цвет связей: нейтральный, чтобы на схеме выделялись блоки, а не провода. */
export const EDGE_COLOR = "#6f6f76"

export const NODE_ICONS: Record<NodeType, LucideIcon> = {
	start: Play,
	message: MessageSquare,
	buttons: LayoutGrid,
	question: MessageCircleQuestion,
	set_variable: Variable,
	wait: Clock,
	goto: CornerDownRight,
	condition: GitBranch,
	notify: Bell,
	http_request: Globe,
	check_subscription: UserCheck,
	random: Shuffle,
	invoice: CreditCard,
	tag: Tag,
}

/** Что можно добавить через «＋ Блок»; в меню группируется по категориям в этом порядке. */
export const CREATABLE_BLOCKS: { type: Exclude<NodeType, "start">; description: string }[] = [
	{ type: "message", description: "Текст, фото, видео, голосовое, кружок, альбом" },
	{ type: "buttons", description: "Кнопки под сообщением или вместо клавиатуры" },
	{ type: "question", description: "Текст, контакт, геолокация или файл в переменную" },
	{ type: "condition", description: "Ветка «да / нет» по переменным и времени" },
	{ type: "invoice", description: "Оплата картой через ЮKassa с чеком" },
	{ type: "check_subscription", description: "Подписан ли пользователь на канал" },
	{ type: "random", description: "Случайная ветка для A/B-теста" },
	{ type: "set_variable", description: "Записать, прибавить или очистить" },
	{ type: "notify", description: "Сообщение админам о заявке" },
	{ type: "http_request", description: "Запрос во внешний сервис, CRM или готовый запрос КМК" },
	{ type: "wait", description: "Продолжить через время" },
	{ type: "goto", description: "Перейти в другую воронку" },
]

/** Id кнопки: 8 символов, подходит под BUTTON_ID_PATTERN. */
const ID_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-"
export function makeButtonId(): string {
	const bytes = crypto.getRandomValues(new Uint8Array(8))
	return Array.from(bytes, (byte) => ID_ALPHABET[byte % ID_ALPHABET.length]).join("")
}
